import styled from '@emotion/styled';
import {
    ActionIcon,
    Anchor,
    Badge,
    Box,
    Button,
    Card,
    Code,
    Divider,
    Group,
    Loader,
    MantineColor,
    Modal,
    Skeleton,
    Space,
    Stack,
    Tabs,
    Text,
    TextInput,
    Title,
    Tooltip,
} from '@mantine/core';
import { PageContent, PagePanel, PanelBody, PaneledPage } from '@root/Design/Layout';
import { CustomColors, theme } from '@root/Design/Themes';
import { endpoint } from '@root/Services/Router/EndpointRegistry';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useModals } from '@mantine/modals';
import { useDi, useDiComponent, useDiContainer } from '@root/Services/DI';
import { AdminInviteModal } from './AdminInviteModal';
import { NotificationService } from '@root/Services/Notification/NotificationService';
import { validate as isGuid } from 'uuid';
import {
    getConnectionTryAssumeRole,
    postConnectionGetPendingConnection,
    getConnectionGetCliConnectionInfo,
    postConnectionAuthorizeAzureConnection,
    getConnectionGetAzureConnectionStatus,
    postConnectionCompleteAzureConnection,
    postConnectionGetAzureSubscriptionConnectabilityStatus,
    getConnectionGetGcpConnectionStatus,
    postConnectionAuthorizeGcpConnection,
    postConnectionGetGcpProjectConnectabilityStatus,
    postConnectionCompleteGcpConnection,
    postConnectionGetGcpBillingInfo,
} from '@apis/Resources';
import {
    CliConnectionInfo,
    PendingCliConnection,
    AzureConnectionRequest,
    AzureConnectionStatus,
    AzureSubscription,
    GcpConnectionStatus,
    GcpProject,
    GcpConnectionRequest,
    GcpBillingInfo,
    GcpBillingTableData,
    GcpBillingExportInfo,
} from '@apis/Resources/model';
import { useCompany } from '@root/Components/Router/CompanyContent';
import {
    LoadingState,
    WizardFormStep,
    ScriptTextArea,
    LoadingValidation,
    WizardFormFooter,
    WizardDownload,
} from '@root/Components/Wizard/WizardForm';
import { ConfigService } from '@root/Services/ConfigService';
import { useNav } from '@root/Services/NavigationService';
import { AlertTriangle, ChevronDown, ChevronLeft, ChevronUp, ChevronRight, ExternalLink, X } from 'tabler-icons-react';
import { AccountIdValidator, ConnectionWizardFooterText } from './Common';
import { IacConnector } from '@root/Components/Connections/IacConnector';
import { EventEmitter, useEvent, useEventValue } from '@root/Services/EventEmitter';
import { useInputState } from '@mantine/hooks';
import { AnchorButton, ToolTip, TooltipWhite } from '@root/Design/Primitives';
import { Link } from '@root/Services/Router/Links';
import { CspConnectionService } from '@root/Services/Connections/CspConnectionService';
import { DataGrid } from '@root/Components/DataGrid';
import { DataColumnConfig } from '@root/Components/DataGrid/Models';
import { JobService, TJob } from '@root/Services/Jobs/JobService';
import { PollingService } from '@root/Services/Jobs/PollingService';
import { DataGridModel } from '@root/Components/DataGrid/DataGridModel';
import { AsyncBundler, RequestThrottlingService } from '@root/Services/AsyncBundler';
import { Select } from '@mantine/core';
import { GridFlexCell } from '@root/Components/DataGrid/Design';
import { GcpBillingAccount } from '@apis/Customers/model';
import { FillerSwitch } from '@root/Design/Filler';

interface IProps {
    isSelected: boolean;
}

interface BillingAccountConfig {
    billingAccountId: string;
    billingAccountName: string;
    projectId: string;
    projectIds: string[];
    allProjectIds: string[]; // Original complete list of project IDs for display
    datasetId: string;
    tableId: string;
    availableDatasets: string[];
    availableTables: { [datasetId: string]: Array<{ tableId: string; hasRequiredSchema: boolean }> };
    tableValidationStatus: { [tableId: string]: boolean };
}

export const PlatformButtonWizard = styled.div<IProps>`
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 0 ${(p) => p.theme.spacing.md}px;
    line-height: 40px;
    border: 1px solid ${(p) => p.theme.colors?.gray?.[3] as CustomColors};
    border-radius: 10px;
    width: 140px;
    height: 96px;
    color: ${(p) => p.theme.black};
    text-decoration: none;
    box-shadow: ${(p) => p.theme.shadows.sm};
    background: ${(props) => (props.isSelected ? (p) => p.theme.colors?.primary?.[6] as CustomColors : (p) => p.theme.white as CustomColors)};
    position: relative;
    z-index: 2;
    &:hover {
        border-color: ${(p) => p.theme.colors.primary[4]};
        box-shadow: 0 0 2px 2px ${(p) => p.theme.colors?.primary?.[2]};
        cursor: pointer;
    }
`;

export const ConnectionButtonWizard = styled.div<IProps>`
    display: block;
    padding: ${(p) => p.theme.spacing.sm}px ${(p) => p.theme.spacing.xl}px;
    border: 1px solid ${(p) => (!p.isSelected ? p.theme.colors?.gray?.[3] : p.theme.colors.primary[6])};
    border-radius: 10px;
    color: ${(props) => (!props.isSelected ? (p) => p.theme.colors?.primary?.[6] as CustomColors : (p) => p.theme.white as CustomColors)};
    text-decoration: none;
    background: ${(props) => (props.isSelected ? (p) => p.theme.colors?.primary?.[6] as CustomColors : (p) => p.theme.white as CustomColors)};
    &:hover {
        border-color: ${(p) => (!p.isSelected ? p.theme.colors.primary[4] : '#0000')};
        cursor: pointer;
    }
    box-shadow: ${(p) => p.theme.shadows.sm};
    position: relative;
    z-index: 2;
`;

export const BottomArrowWizard = styled.div<IProps>`
    position: relative;
    display: inline-block;
    &:after {
        content: '';
        position: absolute;
        left: 0;
        top: 100%;
        right: 0;
        margin: 0 auto;
        width: 0;
        height: 0;
        margin-top: -1px;
        visibility: ${(props) => (props.isSelected ? 'visible' : 'hidden')};
        border-top: 25px solid
            ${(props) => (props.isSelected ? (p) => p.theme.colors?.primary?.[6] as CustomColors : (p) => p.theme.colors?.gray[0] as CustomColors)};
        border-left: 20px solid transparent;
        border-right: 20px solid transparent;
        z-index: 3;
        pointer-events: none;
    }
`;

export const HelpButtonWizard = styled.div`
    height: 26px;
    width: 26px;
    display: flex;
    justify-content: center;
    align-items: center;
    background: ${(p) => p.theme.colors?.warning?.[4] as CustomColors};
    color: ${(p) => p.theme.white as CustomColors};
    font-size: 16px;
    border-radius: 15px;
    cursor: pointer;
`;

export const ConnectionMethodInstructions = styled.div`
    background-color: ${(p) => p.theme.white as CustomColors};
    border-top: 3px solid ${(p) => p.theme.colors?.primary?.[6] as CustomColors};
    width: 100%;
    margin-top: -3px;
    padding: 16px;
    box-shadow: ${(p) => p.theme.shadows.sm};
`;

export const H3 = styled.div`
    border-bottom: 1px solid ${(p) => p.theme.colors?.gray?.[4] as CustomColors};
    color: ${(p) => p.theme.colors?.gray?.[6] as CustomColors};
    font-size: 20px;
`;

export function PlatformBoxWizard({ platform, selected }: { platform: string; selected: boolean }) {
    return (
        <Box p="md">
            {platform === CloudService.AWS ? (
                <img src="/assets/Amazon_Web_Services_Logo.svg" style={{ height: 54, filter: selected ? 'brightness(0) invert(1)' : undefined }} />
            ) : platform === CloudService.Azure ? (
                <img src="/assets/azure_logo.svg" style={{ width: 61, filter: selected ? 'grayscale() brightness(2)' : undefined }} />
            ) : (
                <img src="/assets/google_cloud_logo.svg" style={{ height: 54, filter: selected ? 'grayscale() brightness(2)' : undefined }} />
            )}
        </Box>
    );
}

export function ConnectionBox({ method, selected }: { method: string; selected: boolean }) {
    return (
        <Box p="md">
            {method === 'CLI' ? (
                <img src="/assets/cli.svg" style={{ filter: selected ? 'invert(1)' : 'invert(1) brightness(0.5)', height: '100px' }} />
            ) : method === 'CloudFormation' ? (
                <img src="/assets/aws-cloudformation.svg" style={{ filter: selected ? 'brightness(2) grayscale(1)' : undefined, height: '100px' }} />
            ) : method === 'TerraForm' ? (
                <img src="/assets/terraformio-icon.svg" style={{ filter: selected ? 'brightness(0) invert(1)' : undefined, height: '100px' }} />
            ) : (
                <img src="/assets/azure_arm.png" style={{ filter: selected ? 'brightness(0) invert(1)' : undefined, height: '100px' }} />
            )}
        </Box>
    );
}

export function WhatYoullNeed({ text, info }: { text: string; info: string }) {
    return (
        <Group spacing={0}>
            <Text color="success" size="xl">
                <i className="ti ti-check"></i>
            </Text>
            <Space w="xs" />
            <Text>{text}</Text>
            <ToolTip infoText={<Text sx={{ maxWidth: 500 }}>{info}</Text>} infoSize="small"></ToolTip>
        </Group>
    );
}

export function MethodHelp({ href, icon, text }: { href: string; icon: string; text: string }) {
    return (
        <Text>
            <a href={href} target="_blank" style={{ textDecoration: 'none', color: theme.colors?.primary?.[6] }}>
                <Box style={{ width: '40px', display: 'inline-block', textAlign: 'center' }}>
                    <i className={icon}></i>
                </Box>
                {text}
            </a>
        </Text>
    );
}

enum CloudService {
    AWS = 'AWS',
    Azure = 'Azure',
    GCP = 'GCP',
}

enum ConnectionMethod {
    CLI = 'CLI',
    TerraForm = 'TerraForm',
    CloudFormation = 'CloudFormation',
    ResourceManager = 'ResourceManager',
    Web = 'Web',
}

export function ConnectionWizard() {
    const [helpOpen, setHelpOpen] = useState(true);
    const [cloudService, setCloudService] = useState<string>('');
    const [connectionMethod, setConnectionMethod] = useState<string>('');

    const emailToInvite = '';
    const fiveMinutes = 'Average time to complete: 5 minutes';
    const tenMinutes = 'Average time to complete: 10 minutes';
    const sidebarTitle = 'Guide - Connection Wizard';

    const modals = useModals();
    const DiContainer = useDiComponent();
    const openAdminInviteModal = (type: string) => {
        const id = modals.openModal({
            title: 'Invite ' + cloudService + ' Administrator',
            zIndex: 1000,
            children: (
                <DiContainer>
                    <AdminInviteModal onClose={() => modals.closeModal(id)} type={type}></AdminInviteModal>
                </DiContainer>
            ),
        });
    };

    function choosePlatform(platform: string) {
        setCloudService(platform);
        if (platform == CloudService.GCP) {
            setConnectionMethod(ConnectionMethod.Web);
        } else {
            setConnectionMethod('');
        }
    }

    return (
        <PageContent>
            <PaneledPage>
                <PagePanel size="fill">
                    <PanelBody>
                        <Box style={{ textAlign: 'center' }}>
                            <Title order={1}>Let's Get Connected</Title>
                            <Space h="md" />
                            <Title order={5}>Select a cloud service to begin.</Title>
                            <Space h="md" />
                            <Group position="center">
                                <Tooltip label={CloudService.AWS + ' cloud service'}>
                                    <PlatformButtonWizard
                                        isSelected={cloudService == CloudService.AWS}
                                        onClick={() => choosePlatform(CloudService.AWS)}
                                        data-atid="AWSButton"
                                    >
                                        <PlatformBoxWizard platform={CloudService.AWS} selected={cloudService == CloudService.AWS} />
                                    </PlatformButtonWizard>
                                </Tooltip>
                                <Tooltip label={'Microsoft ' + CloudService.Azure + ' cloud service'}>
                                    <PlatformButtonWizard
                                        isSelected={cloudService == CloudService.Azure}
                                        onClick={() => choosePlatform(CloudService.Azure)}
                                        data-atid="AzureButton"
                                    >
                                        <PlatformBoxWizard platform={CloudService.Azure} selected={cloudService == CloudService.Azure} />
                                    </PlatformButtonWizard>
                                </Tooltip>
                                <Tooltip label={'Google ' + CloudService.GCP + ' cloud service'}>
                                    <BottomArrowWizard isSelected={cloudService == CloudService.GCP}>
                                        <PlatformButtonWizard
                                            isSelected={cloudService == CloudService.GCP}
                                            onClick={() => choosePlatform(CloudService.GCP)}
                                            data-atid="GcpButton"
                                        >
                                            <PlatformBoxWizard platform={CloudService.GCP} selected={cloudService == CloudService.GCP} />
                                        </PlatformButtonWizard>
                                    </BottomArrowWizard>
                                </Tooltip>
                            </Group>
                        </Box>

                        <Space h={50} />
                        {cloudService == CloudService.AWS ? (
                            <>
                                <Box style={{ textAlign: 'center' }}>
                                    Select a method to connect an account.
                                    <Space h="lg" />
                                    <Group position="center">
                                        <div style={{ position: 'relative' }}>
                                            <BottomArrowWizard isSelected={connectionMethod == ConnectionMethod.CLI}>
                                                <ConnectionButtonWizard
                                                    isSelected={connectionMethod == ConnectionMethod.CLI}
                                                    onClick={() => setConnectionMethod(ConnectionMethod.CLI)}
                                                    data-atid="CLIButton"
                                                >
                                                    <ConnectionBox
                                                        method={ConnectionMethod.CLI}
                                                        selected={connectionMethod == ConnectionMethod.CLI}
                                                    />
                                                    {ConnectionMethod.CLI}
                                                </ConnectionButtonWizard>
                                            </BottomArrowWizard>
                                        </div>

                                        <div style={{ position: 'relative' }}>
                                            <BottomArrowWizard isSelected={connectionMethod == ConnectionMethod.CloudFormation}>
                                                <ConnectionButtonWizard
                                                    isSelected={connectionMethod == ConnectionMethod.CloudFormation}
                                                    onClick={() => setConnectionMethod(ConnectionMethod.CloudFormation)}
                                                    data-atid="CloudFormationButton"
                                                >
                                                    <ConnectionBox
                                                        method={ConnectionMethod.CloudFormation}
                                                        selected={connectionMethod == ConnectionMethod.CloudFormation}
                                                    />
                                                    {ConnectionMethod.CloudFormation}
                                                </ConnectionButtonWizard>
                                            </BottomArrowWizard>
                                        </div>
                                        <div style={{ position: 'relative' }}>
                                            <BottomArrowWizard isSelected={connectionMethod == ConnectionMethod.TerraForm}>
                                                <ConnectionButtonWizard
                                                    isSelected={connectionMethod == ConnectionMethod.TerraForm}
                                                    onClick={() => setConnectionMethod(ConnectionMethod.TerraForm)}
                                                    data-atid="TerraFormButton"
                                                >
                                                    <ConnectionBox
                                                        method={ConnectionMethod.TerraForm}
                                                        selected={connectionMethod == ConnectionMethod.TerraForm}
                                                    />
                                                    {ConnectionMethod.TerraForm}
                                                </ConnectionButtonWizard>
                                            </BottomArrowWizard>
                                        </div>
                                    </Group>
                                </Box>
                                <Space h={26} />

                                {connectionMethod == ConnectionMethod.CLI ? (
                                    <ConnectionMethodInstructions>
                                        <Box style={{ textAlign: 'center' }}>{fiveMinutes}</Box>
                                        <ConnectCliContentWizard platform="aws" />
                                    </ConnectionMethodInstructions>
                                ) : null}
                                {connectionMethod == ConnectionMethod.CloudFormation ? (
                                    <ConnectionMethodInstructions>
                                        <Box style={{ textAlign: 'center' }}>{tenMinutes}</Box>
                                        <ConnectCfContentWizard />
                                    </ConnectionMethodInstructions>
                                ) : null}
                                {connectionMethod == ConnectionMethod.TerraForm ? (
                                    <ConnectionMethodInstructions>
                                        <Box style={{ textAlign: 'center' }}>{tenMinutes}</Box>
                                        <ConnectTfContentWizard />
                                    </ConnectionMethodInstructions>
                                ) : null}
                            </>
                        ) : null}
                        {cloudService == CloudService.Azure ? (
                            <>
                                <Box style={{ textAlign: 'center' }}>
                                    Select a method to connect a subscription.
                                    <Space h="lg" />
                                    <Group position="center">
                                        <div style={{ position: 'relative' }}>
                                            <BottomArrowWizard isSelected={connectionMethod == ConnectionMethod.Web}>
                                                <ConnectionButtonWizard
                                                    isSelected={connectionMethod == ConnectionMethod.Web}
                                                    onClick={() => setConnectionMethod(ConnectionMethod.Web)}
                                                >
                                                    <ConnectionBox
                                                        method={ConnectionMethod.Web}
                                                        selected={connectionMethod == ConnectionMethod.Web}
                                                    />
                                                    {ConnectionMethod.Web}
                                                </ConnectionButtonWizard>
                                            </BottomArrowWizard>
                                        </div>
                                        <div style={{ position: 'relative' }}>
                                            <BottomArrowWizard isSelected={connectionMethod == ConnectionMethod.CLI}>
                                                <ConnectionButtonWizard
                                                    isSelected={connectionMethod == ConnectionMethod.CLI}
                                                    onClick={() => setConnectionMethod(ConnectionMethod.CLI)}
                                                >
                                                    <ConnectionBox
                                                        method={ConnectionMethod.CLI}
                                                        selected={connectionMethod == ConnectionMethod.CLI}
                                                    />
                                                    CLI
                                                </ConnectionButtonWizard>
                                            </BottomArrowWizard>
                                        </div>
                                    </Group>
                                </Box>
                                <Space h={26} />
                                {connectionMethod == ConnectionMethod.Web ? (
                                    <ConnectionMethodInstructions>
                                        <Box style={{ textAlign: 'center' }}>{fiveMinutes}</Box>
                                        <ConnectAzureTenantWizard />
                                    </ConnectionMethodInstructions>
                                ) : null}
                                {connectionMethod == ConnectionMethod.CLI ? (
                                    <ConnectionMethodInstructions>
                                        <Box style={{ textAlign: 'center' }}>{fiveMinutes}</Box>
                                        <ConnectCliContentWizard platform="azure" />
                                    </ConnectionMethodInstructions>
                                ) : null}
                            </>
                        ) : null}
                        {cloudService == CloudService.GCP ? (
                            <>
                                <ConnectionMethodInstructions>
                                    <Box style={{ textAlign: 'center' }}>{fiveMinutes}</Box>
                                    <ConnectGcpWizard />
                                </ConnectionMethodInstructions>
                            </>
                        ) : null}
                    </PanelBody>
                    <Divider />
                </PagePanel>
                <Divider orientation="vertical" />
                {helpOpen ? (
                    <PagePanel size={440}>
                        <Group position="apart" style={{ borderBottom: '1px solid ' + theme.colors?.gray?.[4] }}>
                            <Title p="lg" order={3}>
                                {sidebarTitle}
                            </Title>
                            <Box style={{ marginRight: '16px' }}>
                                <HelpButtonWizard onClick={() => setHelpOpen(false)}>
                                    <ChevronLeft size={20} />
                                </HelpButtonWizard>
                            </Box>
                        </Group>
                        <Box p="md">
                            <Space h="lg" />
                            <H3>
                                {cloudService == '' ? 'Overview' : cloudService} {cloudService == CloudService.Azure ? `- ${connectionMethod}` : ''}
                            </H3>
                            <Space h="md" />
                            <Text weight={700}>{cloudService == '' ? 'Select a cloud service' : "What You'll Need"}</Text>
                            <Space h="md" />
                            {cloudService == '' ? (
                                <Text style={{ fontSize: '14px' }}>
                                    CloudSaver supports {CloudService.AWS} and {CloudService.Azure}. Select one of the cloud services to connect your
                                    account.
                                </Text>
                            ) : (
                                <>
                                    <Text size="sm">
                                        {cloudService == CloudService.AWS ? (
                                            <>
                                                <WhatYoullNeed
                                                    text={'Your ' + cloudService + ' Management Account ID'}
                                                    info="Also known as master account or master payor, we'll need to connect to this account for resource information."
                                                />
                                                <Space h="xs" />
                                                <WhatYoullNeed
                                                    text="Management account admin login credentials"
                                                    info="You'll need access to the management account to deploy the connection"
                                                />
                                                <Space h="xs" />
                                                <WhatYoullNeed
                                                    text="Permission to create IAM roles and policies"
                                                    info="We'll use roles you create to make the connection"
                                                />
                                            </>
                                        ) : cloudService == CloudService.Azure ? (
                                            <>
                                                {connectionMethod === ConnectionMethod.Web ? (
                                                    <WhatYoullNeed
                                                        text="Sign in & consent (opens Microsoft popup)"
                                                        info="We’ll open a login.microsoftonline.com window to request access on behalf of CloudSaver. If you’re a tenant admin you can consent for the organization; otherwise an admin may need to approve later."
                                                    />
                                                ) : (
                                                    <WhatYoullNeed
                                                        text={cloudService + ' CLI installed and configured'}
                                                        info={'CloudSaver will use CLI to connect to your ' + cloudService + ' subscription.'}
                                                    />
                                                )}
                                                <Space h="xs" />
                                                <WhatYoullNeed
                                                    text={'App Administrator ' + cloudService + ' AD or equivalent role'}
                                                    info={
                                                        "You'll need to be the Application Administrator " +
                                                        cloudService +
                                                        ' AD or have a role that allows application registration.'
                                                    }
                                                />
                                                <Space h="xs" />
                                                <WhatYoullNeed
                                                    text="Permission to create custom roles"
                                                    info="You'll need to have permissions that allow creation of custom role/permissions on the subscription level/scope."
                                                />
                                                <Space h="xs" />
                                                {connectionMethod === ConnectionMethod.Web ? (
                                                    <>
                                                        <WhatYoullNeed
                                                            text="Subscription IDs to connect"
                                                            info="After consent completes, you’ll select one or more subscriptions from the list to enable exports."
                                                        />
                                                        <Space h="xs" />
                                                        <WhatYoullNeed
                                                            text="Provider registration allowed"
                                                            info="The subscription must permit registering Microsoft.Storage and Microsoft.CostManagement resource providers (we’ll register them if needed)."
                                                        />
                                                        <Space h="xs" />
                                                        <WhatYoullNeed
                                                            text="Browser pop-ups enabled"
                                                            info="Allow pop-ups for login.microsoftonline.com so the consent window can open."
                                                        />
                                                    </>
                                                ) : (
                                                    <></>
                                                )}
                                            </>
                                        ) : (
                                            <>
                                                <WhatYoullNeed
                                                    text="Sign in & consent (opens Google popup)"
                                                    info="We’ll open a accounts.google.com window to request access on behalf of CloudSaver. If you’re an admin you can consent for the organization; otherwise an admin may need to approve later."
                                                />
                                                <Space h="xs" />
                                                <WhatYoullNeed
                                                    text={'Storage Admin or equivalent role'}
                                                    info={'You will need storage.buckets.create on the project level.'}
                                                />
                                                <Space h="xs" />
                                                <WhatYoullNeed
                                                    text="Permission to create custom roles"
                                                    info="You'll need to have permissions that allow creation of custom role/permissions on the organization level or project level, if no organization provided. What we need: iam.roles.create, iam.roles.update, resourcemanager.organizations.setIamPolicy, resourcemanager.organizations.getIamPolicy."
                                                />
                                                <Space h="xs" />
                                                <WhatYoullNeed
                                                    text="Cloud Resource Manager API"
                                                    info="You'll need to have the Cloud Resource Manager API enabled, which can be found under APIs & Services in the Google Cloud Console."
                                                />
                                                <Space h="xs" />
                                                <WhatYoullNeed
                                                    text="Identity and Access Management (IAM) API"
                                                    info="You'll need to have the Identity and Access Management (IAM) API enabled, which can be found under APIs & Services in the Google Cloud Console."
                                                />
                                                <Space h="xs" />
                                                <WhatYoullNeed
                                                    text="Cloud Asset API"
                                                    info="You'll need to have the Cloud Asset API enabled, which can be found under APIs & Services in the Google Cloud Console."
                                                />
                                                <Space h="xs" />
                                                <WhatYoullNeed
                                                    text="Project IDs to connect"
                                                    info="After consent completes, you’ll select one or more project from the list to enable exports."
                                                />
                                                <Space h="xs" />
                                                <WhatYoullNeed
                                                    text="Browser pop-ups enabled"
                                                    info="Allow pop-ups for accounts.google.com so the consent window can open."
                                                />
                                            </>
                                        )}

                                        <Space h="lg" />

                                        <Box style={{ backgroundColor: theme.colors?.gray?.[3], borderRadius: '10px', padding: '20px' }}>
                                            <Text size="md" weight={700}>
                                                Don't Have One of These?
                                            </Text>
                                            <Space h="sm" />
                                            <Text size="sm">Invite your {cloudService} administrator and team members to CloudSaver</Text>
                                            <Space h="sm" />
                                            <Button
                                                data-atid="UsersInviteButton"
                                                sx={{ width: 100 }}
                                                type="submit"
                                                value={emailToInvite}
                                                onClick={() => openAdminInviteModal('bob')}
                                            >
                                                Invite
                                            </Button>
                                        </Box>
                                    </Text>

                                    {connectionMethod == ConnectionMethod.CLI && cloudService == CloudService.AWS ? (
                                        <>
                                            <Space h="lg" />
                                            <H3>CLI</H3>
                                            <Space h="sm" />
                                            <MethodHelp
                                                href={'/assets/connection/' + cloudService + 'CLI.pdf'}
                                                icon="ti ti-file-type-pdf"
                                                text={'Download ' + ConnectionMethod.CLI + ' Instructions'}
                                            />
                                            <Space h="sm" />
                                            <MethodHelp
                                                href="https://cloudsaver.com/resources/docs/connection-wizard-cli"
                                                icon="ti ti-browser"
                                                text="Knowledge Base Article Link"
                                            />
                                        </>
                                    ) : null}
                                    {connectionMethod == ConnectionMethod.CloudFormation ? (
                                        <>
                                            <Space h="lg" />
                                            <H3>CloudFormation</H3>
                                            <Space h="sm" />
                                            <MethodHelp
                                                href="/assets/connection/CloudFormation.pdf"
                                                icon="ti ti-file-type-pdf"
                                                text={'Download ' + ConnectionMethod.CloudFormation + ' Instructions'}
                                            />
                                            <Space h="sm" />
                                            <MethodHelp
                                                href="https://cloudsaver.com/resources/docs/connecting-accounts-cloudformation"
                                                icon="ti ti-browser"
                                                text="Knowledge Base Article Link"
                                            />
                                        </>
                                    ) : null}
                                    {connectionMethod == ConnectionMethod.TerraForm ? (
                                        <>
                                            <Space h="lg" />
                                            <H3>TerraForm</H3>
                                            <Space h="sm" />
                                            <MethodHelp
                                                href="/assets/connection/TerraForm.pdf"
                                                icon="ti ti-file-type-pdf"
                                                text={'Download ' + ConnectionMethod.TerraForm + ' Instructions'}
                                            />
                                            <Space h="sm" />
                                            <MethodHelp
                                                href="https://cloudsaver.com/resources/docs/connection-wizard-terraform"
                                                icon="ti ti-browser"
                                                text="Knowledge Base Article Link"
                                            />
                                        </>
                                    ) : null}
                                </>
                            )}
                        </Box>
                    </PagePanel>
                ) : (
                    <PagePanel size={50}>
                        <Box style={{ marginLeft: '8px', marginTop: '23px' }}>
                            <HelpButtonWizard onClick={() => setHelpOpen(true)}>
                                <ChevronRight size={20} />
                            </HelpButtonWizard>
                        </Box>

                        <Text
                            style={{
                                transform: 'rotate(270deg)',
                                height: '25px',
                                width: '225px',
                                marginTop: '100px',
                                marginLeft: '-90px',
                            }}
                        >
                            {sidebarTitle}
                        </Text>
                    </PagePanel>
                )}
            </PaneledPage>
        </PageContent>
    );
}
endpoint('connections-wizard', ConnectionWizard, 'Connection Wizard');

export function ConnectCliContentWizard({
    hideFooter,
    onReadyChanged,
    ...props
}: {
    hideFooter?: boolean;
    platform: string;
    onReadyChanged?: (ready: boolean, accountId: string) => void;
}) {
    const platform = props.platform ?? useNav().getData('platform')?.platform;
    const { getAscendUrl } = useNav();
    const configSvc = useDi(ConfigService);
    const company = useCompany();

    const [connState, setConnState] = useState<LoadingState>(LoadingState.loading);
    const [connMsg, setConnMsg] = useState<string>('Awaiting connection');
    const [cliScriptWin, setCliScriptWin] = useState<string>('');
    const [cliScriptWinReadonly, setCliScriptWinReadonly] = useState<string>('');
    const cspConnSvc = useDi(CspConnectionService);
    const connChecker = useMemo(
        () => ({
            connInfo: null as CliConnectionInfo | null,
            pendingConn: null as PendingCliConnection | null,
            shouldPoll: true,
            poll() {
                setTimeout(async () => {
                    if (!this.shouldPoll) {
                        return;
                    }
                    try {
                        if (this.connInfo) {
                            if (this.pendingConn && this.pendingConn.Complete) {
                                setConnState(LoadingState.check);
                                setConnMsg('Connection successful');
                                this.shouldPoll = false;
                                cspConnSvc.connectionStateChanged.emit();
                            } else if (this.pendingConn && this.pendingConn.MasterAccount && this.pendingConn.ExternalId) {
                                const assumeResult = await getConnectionTryAssumeRole({
                                    awsAccountId: this.pendingConn.MasterAccount,
                                    externalId: this.pendingConn.ExternalId,
                                });
                                if (assumeResult?.Success) {
                                    setConnState(LoadingState.check);
                                    setConnMsg('Connection successful');
                                    this.shouldPoll = false;
                                }
                            } else {
                                this.pendingConn = await postConnectionGetPendingConnection({ token: this.connInfo.Token! });
                            }
                        }
                    } finally {
                        this.poll();
                    }
                }, 3000);
            },
            stopPolling() {
                this.shouldPoll = false;
            },
        }),
        []
    );

    useEffect(() => {
        (async () => {
            const baseUrl = configSvc.config.apis['Resources'].baseUrl;
            const connInfo = await getConnectionGetCliConnectionInfo();
            connChecker.connInfo = connInfo;
            const [, creds] = (connInfo.Creds ?? '').split(' ');
            const [name, value] = creds.replace(/"/g, '').split('=');
            const domain = new URL(baseUrl).hostname;
            const windowsCli = `$session=New-Object Microsoft.PowerShell.Commands.WebRequestSession; $cookie=New-Object System.Net.Cookie; $cookie.Name='${name}'; $cookie.Domain='${domain}'; $cookie.Value='${value}'; $session.Cookies.Add($cookie); iwr ${baseUrl}/connection/scripts/${
                connInfo.Token
            }/${platform?.toLowerCase()}-cs-connector.ps1 -WebSession $session -OutFile ${platform?.toLowerCase()}-cs-connector.ps1;  .\\${platform?.toLowerCase()}-cs-connector.ps1`;
            setCliScriptWin(windowsCli);
            setCliScriptWinReadonly(windowsCli + ' -ReadOnly');
        })();
    }, []);

    useEffect(() => {
        connChecker.poll();
        return () => connChecker.stopPolling();
    }, []);

    useEffect(() => {
        onReadyChanged?.(connState === LoadingState.check, connChecker.pendingConn?.MasterAccount ?? '');
    }, [connState === LoadingState.check]);

    return (
        <>
            <WizardFormStep title="1. Copy and paste the script into your preferred CLI" infoText="Bash or Powershell" infoSize="small">
                <Space h="md" />
                <Tabs defaultValue="windows">
                    <Tabs.List>
                        <Tabs.Tab value="windows">Windows</Tabs.Tab>
                        {platform !== 'azure' ? null : <Tabs.Tab value="windows-readonly">Windows - Read Only</Tabs.Tab>}
                    </Tabs.List>
                    <Tabs.Panel value="windows">
                        <ScriptTextArea value={cliScriptWin} label="script area" minRows={3} maxRows={4} />
                    </Tabs.Panel>
                    <Tabs.Panel value="windows-readonly">
                        <ScriptTextArea value={cliScriptWinReadonly} label="script area" minRows={3} maxRows={4} />
                    </Tabs.Panel>
                </Tabs>
            </WizardFormStep>
            <WizardFormStep
                title="2. CloudSaver will detect when the script is finished"
                subTitle="We'll check for the script to run and for accounts to be connected."
            >
                <Box ml="lg">
                    <LoadingValidation loadingState={connState} text={connMsg}></LoadingValidation>
                </Box>
            </WizardFormStep>
            {hideFooter ? null : (
                <>
                    <WizardFormFooter>
                        <Group position="apart">
                            <ConnectionWizardFooterText company={company!} />
                            <Button
                                component={Link}
                                href={getAscendUrl()}
                                disabled={connState !== LoadingState.check}
                                style={{ pointerEvents: connState !== LoadingState.check ? 'none' : undefined }}
                            >
                                View Connections
                            </Button>
                        </Group>
                    </WizardFormFooter>
                </>
            )}
        </>
    );
}

export function ConnectCfContentWizard({
    hideFooter,
    onReadyChanged,
}: {
    hideFooter?: boolean;
    onReadyChanged?: (ready: boolean, accountId: string) => void;
}) {
    const di = useDiContainer();
    const iacConn = useMemo(() => di.resolve(IacConnector), []);
    const company = useCompany();
    const { move } = useNav();

    useEvent(iacConn.status);
    const [accountId, setAccountId] = useInputState('');
    useEffect(() => {
        iacConn.updateAccount(accountId, company!.UniqueId!);
    }, [accountId]);
    function stackSets() {
        window.open('https://docs.aws.amazon.com/AWSCloudFormation/latest/UserGuide/what-is-cfnstacksets.html');
    }
    useEffect(() => {
        return () => {
            iacConn.dispose();
        };
    }, []);
    useEffect(() => {
        onReadyChanged?.(iacConn.isConnected(), accountId);
    }, [iacConn.isConnected()]);
    return (
        <>
            <WizardFormStep
                title="1. Enter your 12-digit Management Account ID"
                infoText="Also known as master account or master payor, we'll need to connect to this account for resource information."
                infoSize="small"
                subTitle="We use this to find and connect to your AWS Management Account."
            >
                <Group spacing="xs" ml="xl">
                    <TextInput
                        value={accountId}
                        onChange={setAccountId}
                        size="sm"
                        type="number"
                        maxLength={12}
                        placeholder="012345678910"
                        sx={{ width: 200 }}
                        data-atid="AccountIdInput"
                    />
                    <AccountIdValidator condition={accountId.length == 12} />
                </Group>
            </WizardFormStep>
            <WizardFormStep
                title="2. Upload the .yaml file to the Management Account."
                subTitle={
                    <>
                        Use <Anchor onClick={stackSets}>stack sets</Anchor> to apply to all accounts you want to connect to CloudSaver.
                    </>
                }
            >
                <Box ml="xl">
                    <WizardDownload
                        imgLocation="/assets/aws-cloudformation.svg"
                        displayTitle="CloudFormation"
                        displayText="Download YAML"
                        fileLocation={iacConn.getDownloadUrl('cs-cloudformation.yml')}
                        disable={!iacConn.isAccountValid()}
                        disabledText="Account ID is Required"
                        atid="CloudFormationDownload"
                    ></WizardDownload>
                </Box>
            </WizardFormStep>
            <WizardFormStep
                title="3. CloudSaver will check for connectivity to the Management Account."
                subTitle="We will use your entered Management Account ID to attempt connection every 3 seconds."
            >
                <Box ml="xl">
                    <LoadingValidation loadingState={iacConn.getLoadingState()} text={iacConn.getStatusMessage()}></LoadingValidation>
                </Box>
            </WizardFormStep>
            {hideFooter ? null : (
                <>
                    <WizardFormFooter>
                        <Group position="apart">
                            <ConnectionWizardFooterText company={company!} />
                            <Button
                                disabled={!iacConn.isConnected()}
                                component={Link}
                                onClick={() => move('connections')}
                                style={{ pointerEvents: !iacConn.isConnected() ? 'none' : undefined }}
                            >
                                View Connections
                            </Button>
                        </Group>
                    </WizardFormFooter>
                </>
            )}
        </>
    );
}

export function ConnectTfContentWizard({
    hideFooter,
    onReadyChanged,
}: {
    hideFooter?: boolean;
    onReadyChanged?: (ready: boolean, accountId: string) => void;
}) {
    const di = useDiContainer();
    const iacConn = useMemo(() => di.resolve(IacConnector), []);
    const { getMoveUrl } = useNav();
    const company = useCompany();

    useEvent(iacConn.status);
    const [accountId, setAccountId] = useInputState('');
    useEffect(() => {
        iacConn.updateAccount(accountId, company!.UniqueId!);
    }, [accountId]);

    useEffect(() => {
        return () => {
            iacConn.dispose();
        };
    }, []);

    useEffect(() => {
        onReadyChanged?.(iacConn.isConnected(), accountId);
    }, [iacConn.isConnected()]);

    return (
        <>
            <WizardFormStep
                title="1. Enter your 12-digit Management Account ID"
                infoText="Also known as master account or master payor, we'll need to connect to this account for resource information."
                infoSize="small"
                subTitle="We use this to find and connect CloudSaver to your AWS Management Account."
            >
                <Group spacing="xs" ml="xl">
                    <TextInput value={accountId} onChange={setAccountId} size="sm" type="number" placeholder="012345678910" sx={{ width: 200 }} />
                    <AccountIdValidator condition={accountId.length == 12} />
                </Group>
            </WizardFormStep>
            <WizardFormStep
                title="2. Deploy the .tf file to your accounts."
                subTitle="Start with the Management Account, then deploy to all other accounts you want to connect to CloudSaver. A Management Account is required."
            >
                <Box ml="xl">
                    <WizardDownload
                        imgLocation="/assets/terraformio-icon.svg"
                        displayTitle="Terraform"
                        displayText="Download .tf"
                        fileLocation={iacConn.getDownloadUrl('cs-terraform.tf')}
                        disable={!iacConn.isAccountValid()}
                        disabledText="Account ID is Required"
                    ></WizardDownload>
                </Box>
            </WizardFormStep>
            <WizardFormStep
                title="3. CloudSaver will check for connectivity to the Management Account."
                subTitle="Using the Management Account ID you entered, we will attempt to connect every 3 seconds until success."
            >
                <Box ml="xl">
                    <LoadingValidation loadingState={iacConn.getLoadingState()} text={iacConn.getStatusMessage()}></LoadingValidation>
                </Box>
            </WizardFormStep>
            {!hideFooter && (
                <>
                    <WizardFormFooter>
                        <Group position="apart">
                            <ConnectionWizardFooterText company={company!} />
                            <Button
                                disabled={!iacConn.isConnected()}
                                component={Link}
                                href={getMoveUrl('connections')}
                                style={{ pointerEvents: !iacConn.isConnected() ? 'none' : undefined }}
                            >
                                View connections
                            </Button>
                        </Group>
                    </WizardFormFooter>
                </>
            )}
        </>
    );
}

enum SubDeployStatus {
    Pending = '',
    NotSelected = 'Not Selected',
    Queued = 'Queued',
    Deploying = 'Deploying',
    Error = 'Error',
    Completed = 'Completed',
}

type SubProvisionStatus = Record<string, { status?: SubDeployStatus; error?: string | null; warning?: string }>;
type JobOfAzureSubscriptionProvisioning = TJob<{ SubscriptionId: string }, 'Cloudsaver.Resources.Domain.Azure.Models.AzureSubscriptionProvisioning'>;
export function ConnectAzureTenantWizard({
    hideFooter,
    onReadyChanged,
}: {
    hideFooter?: boolean;
    onReadyChanged?: (ready: boolean, tenantId: string) => void;
}) {
    const { getAscendUrl, goto } = useNav();
    const cspConnSvc = useDi(CspConnectionService);
    const notificationSvc = useDi(NotificationService);
    const jobSvc = useDi(JobService);
    const pollSvc = useDi(PollingService);
    const bundleSvc = useDi(AsyncBundler);
    const configSvc = useDi(ConfigService);

    const [gridModel, setGridModel] = useState<DataGridModel | null>(null);
    const [connState, setConnState] = useState<LoadingState>(LoadingState.wait);
    const [attemptedConnection, setAttemptedConnection] = useState<boolean>(false);
    const [connMsg, setConnMsg] = useState<string>('Awaiting consent');
    const [subs, setSubs] = useState<Array<AzureSubscription>>([]);
    const [selectedSubs, setSelectedSubs] = useState<string[]>([]);
    const [finalizing, setFinalizing] = useState<boolean>(false);
    const readyToFinish = connState === LoadingState.check && selectedSubs.length > 0 && !finalizing;

    const consentState = useMemo(() => {
        const throttleSvc = RequestThrottlingService.create(500);
        return {
            invalidated: EventEmitter.empty(),
            consentUrl: '',
            connectionId: '',
            loading: false,
            tenantId: '',
            async setTenantId(tenantId: string) {
                tenantId = tenantId.trim();
                if (this.tenantId !== tenantId) {
                    this.loading = true;
                    try {
                        this.tenantId = tenantId;
                        this.consentUrl = '';
                        this.connectionId = '';
                        this.invalidated.emit();

                        if (isGuid(this.tenantId)) {
                            const consentUrlResp = (await throttleSvc.throttle(tenantId, () =>
                                postConnectionAuthorizeAzureConnection({ tenantId })
                            )) as unknown as { connectionId: string; authorizeUrl: string };
                            if (this.tenantId === tenantId) {
                                this.consentUrl = consentUrlResp.authorizeUrl;
                                this.connectionId = consentUrlResp.connectionId;
                                this.invalidated.emit();
                            }
                        }
                    } finally {
                        this.loading = false;
                    }
                }
            },
        };
    }, []);
    useEvent(consentState.invalidated);
    const { connectionId, consentUrl, tenantId } = consentState;

    const connectionStatusSvc = useMemo(() => {
        const statusEvt = new EventEmitter<SubProvisionStatus>({});
        return {
            status: statusEvt,
            updateStatuses(updates: { subId: string; status: SubDeployStatus; error?: string | null }[]) {
                statusEvt.emit({
                    ...statusEvt.value,
                    ...updates.reduce((res, u) => {
                        res[u.subId] = { ...statusEvt.value[u.subId], status: u.status, error: u.error };
                        return res;
                    }, {} as SubProvisionStatus),
                });
            },
            loadWarning(subId: string) {
                if (typeof statusEvt.value[subId]?.warning !== 'string') {
                    const getSubStatuses = async (subscriptionIds: string[]) => {
                        const result = await postConnectionGetAzureSubscriptionConnectabilityStatus(subscriptionIds, {
                            connectionId: connectionId ?? '',
                        });
                        const updates = result.reduce((res, u) => {
                            res[u.subscriptionId!] = { ...statusEvt.value[u.subscriptionId!], warning: u.status ?? '' };
                            return res;
                        }, {} as SubProvisionStatus);
                        statusEvt.emit({ ...statusEvt.value, ...updates });
                    };
                    bundleSvc.bundle<string>(connectionId!, subId, getSubStatuses);
                    statusEvt.emit({ ...statusEvt.value, [subId]: { ...statusEvt.value[subId], warning: '' } });
                }
            },
        };
    }, [connectionId]);

    const subscriptionColumns = useMemo(() => {
        const types = new Set(subs.map((r) => r.Type));
        return [
            {
                id: 'Status',
                header: 'Status',
                accessor: (s) => connectionStatusSvc.status.value[s.Id!].status,
                sortField: 'Status',
                type: 'string',
                align: 'center',
                noSort: true,
                defaultWidth: 165,
                cellRenderer: (s) => <AzureWebConnectionStatusCell connectionStatusSvc={connectionStatusSvc} subscription={s} />,
                exportOptions: {
                    hidden: true,
                },
            },
            {
                id: 'Name',
                header: 'Name',
                accessor: (s) => s.Name,
                sortField: 'Name',
                type: 'string',
                defaultWidth: 300,
                filter: {
                    filterType: 'string',
                    filterField: 'Name',
                    name: 'Name',
                },
            },
            {
                id: 'Id',
                header: 'Subscription Id',
                accessor: (s) => s.Id,
                sortField: 'Id',
                type: 'string',
                defaultWidth: 300,
                filter: {
                    filterType: 'string',
                    filterField: 'Id',
                    name: 'Id',
                },
            },
            {
                id: 'Type',
                header: 'Type',
                accessor: (s) => s.Type,
                sortField: 'Type',
                type: 'string',
                defaultWidth: 220,
                filter: {
                    filterType: 'string',
                    filterField: 'Type',
                    name: 'Type',
                    options: {
                        getValueProvider: () => Array.from(types).map((t) => ({ label: t, value: t })),
                    },
                },
            },
        ] as DataColumnConfig<AzureSubscription>[];
    }, [connectionId, subs]);

    const poller = useMemo(
        () => ({
            pendingConn: null as AzureConnectionStatus | null,
            shouldPoll: true,
            async poll() {
                setTimeout(async () => {
                    if (!this.shouldPoll || !connectionId) return;
                    try {
                        if (this.pendingConn && this.pendingConn.Connected) {
                            setConnState(LoadingState.check);
                            setConnMsg('Consent successful');
                            const list =
                                this.pendingConn.Subscriptions?.map((s: AzureSubscription) => ({
                                    Id: s.Id,
                                    Name: s.Name,
                                    Type: s.Type,
                                    Connected: s.Connected,
                                })) ?? [];
                            setSubs(list);
                            this.shouldPoll = false;
                            cspConnSvc.connectionStateChanged.emit();
                        } else {
                            setSubs([]);
                            setSelectedSubs([]);
                            setConnState(LoadingState.wait);
                            this.pendingConn = await getConnectionGetAzureConnectionStatus({ connectionId: connectionId! });
                        }
                    } catch {
                        setConnState(LoadingState.failed);
                        setConnMsg('Error while checking consent');
                    } finally {
                        this.poll();
                    }
                }, 3000);
            },
            stopPolling() {
                this.shouldPoll = false;
            },
        }),
        [connectionId]
    );

    useEffect(() => {
        onReadyChanged?.(readyToFinish, tenantId);
    }, [readyToFinish, tenantId]);

    useEffect(() => {
        if (!connectionId) return;
        poller.shouldPoll = true;
        poller.poll();
        return () => poller.stopPolling();
    }, [connectionId, poller]);

    const openConsentWindow = (event: React.MouseEvent<HTMLAnchorElement, MouseEvent>) => {
        event.preventDefault();
        window.open(consentUrl, '_blank');
    };

    const completeConnection = async () => {
        if (!connectionId) return;
        try {
            setFinalizing(true);

            const request = { ConnectionId: connectionId, TenantId: tenantId, SubscriptionIds: selectedSubs } as AzureConnectionRequest;
            var job = await postConnectionCompleteAzureConnection(request);
            if (job.FailureMessage) {
                notificationSvc.notify('Failed to start connection process', job.FailureMessage ?? '', 'error', null);
            }
            beginJobPolling(job.HierarchyId ?? '');
        } catch {
            setFinalizing(false);
            notificationSvc.notify('Failed to start connection process. Please try again.', '', 'error', null);
        }
    };

    const beginJobPolling = (jobHierarchyId: string) => {
        const poller = pollSvc.pollUntil(
            async () => {
                const jobs = (await jobSvc.queryJobs((q) =>
                    q.where((b) => b.model.HierarchyId!.eq(jobHierarchyId)).take(10000)
                )) as JobOfAzureSubscriptionProvisioning[];

                connectionStatusSvc.updateStatuses(
                    jobs.map((j) => {
                        const status =
                            j.Status === 'Failed'
                                ? SubDeployStatus.Error
                                : j.Status === 'Succeeded'
                                ? SubDeployStatus.Completed
                                : j.Status === 'Started'
                                ? SubDeployStatus.Deploying
                                : SubDeployStatus.Queued;
                        return { subId: j.Parameters.SubscriptionId, status, error: j.FailureMessage };
                    })
                );
                return jobs;
            },
            (jobs) => jobs.length > 0 && jobs.every((j) => j.FinishedAt),
            1500
        );

        poller
            .then(() => {
                setFinalizing(false);
            })
            .catch((err) => {
                setFinalizing(false);
                notificationSvc.notify('Failed to monitor deployments.', String(err ?? ''), 'error', null);
            })
            .finally(() => setAttemptedConnection(true));
    };

    return (
        <>
            <WizardFormStep
                title="1. Enter your Azure Tenant ID"
                subTitle="We'll use this tenant to request user-impersonation consent to connect subscriptions."
                infoText="Example: 11111111-2222-3333-4444-555555555555"
                infoSize="small"
            >
                <Group spacing="xs" ml="xl">
                    <TextInput
                        value={tenantId}
                        onChange={(e) => consentState.setTenantId(e.currentTarget.value)}
                        size="sm"
                        placeholder="00000000-0000-0000-0000-000000000000"
                        sx={{ width: 360 }}
                        data-atid="AzureTenantIdInput"
                        rightSection={consentState.loading ? <Loader size={16} /> : null}
                    />
                </Group>
            </WizardFormStep>
            <WizardFormStep
                title="2. Sign in to Microsoft Account"
                subTitle="A new tab will open, prompting you to log into your Microsoft account and grant consent for Cloudsaver to proceed with the connection."
            >
                <Group spacing="xs" ml="xl">
                    <Button
                        component="a"
                        disabled={!consentUrl}
                        href={consentUrl}
                        onClick={openConsentWindow}
                        rightIcon={<ExternalLink size={16} />}
                        target="_blank"
                        rel="noreferrer"
                        data-atid="AzureConsentButton"
                    >
                        Open Microsoft Consent
                    </Button>
                </Group>
                <Box ml="lg" mt="sm">
                    <LoadingValidation loadingState={connState} text={connMsg} />
                </Box>
            </WizardFormStep>
            <WizardFormStep
                title="3. Select the subscriptions to connect"
                subTitle="After consent is granted, we'll list subscriptions visible to the granted scope."
            >
                <Text size="sm" color="dimmed" ml="xl" mt="xs" mb="sm">
                    Connecting will add or update a custom Role Definition and App Registration, then deploy{' '}
                    <Anchor
                        href={`${configSvc.config.apis.Resources.baseUrl}/connection/scripts/cs-connect-arm.json`}
                        download={'cs-connect-arm.json'}
                    >
                        this ARM template
                    </Anchor>{' '}
                    to each selected subscription.
                </Text>
                <Box ml="xl" sx={{ display: 'flex', flexDirection: 'column', minHeight: 0 }}>
                    {subs.length === 0 ? (
                        <Text size="sm" color="dimmed">
                            No subscriptions available yet. Complete consent first.
                        </Text>
                    ) : (
                        (() => {
                            const visibleRows = Math.min(subs.length, 10);
                            const gridHeight = 150 + visibleRows * 30;
                            const maxHeight = 600;

                            return (
                                <Box
                                    sx={{
                                        height: gridHeight,
                                        maxHeight,
                                        display: 'flex',
                                        flexDirection: 'column',
                                        minHeight: 180,
                                    }}
                                >
                                    <DataGrid
                                        columns={subscriptionColumns}
                                        dataSource={subs}
                                        onModelLoaded={(m) => setGridModel(m)}
                                        selectionMode="multiple"
                                        showCount
                                        showNumSelected
                                        hideColumnSelector
                                        allowSavedViews={false}
                                        rowIdProvider={(r) => r.Id!}
                                        onSelectedChanged={async ({ getItems }) => {
                                            const selected = await getItems();
                                            setSelectedSubs(selected.map((s) => s.Id!));
                                        }}
                                    ></DataGrid>
                                </Box>
                            );
                        })()
                    )}
                </Box>
            </WizardFormStep>
            {hideFooter ? null : (
                <WizardFormFooter>
                    <Group position="apart">
                        {!attemptedConnection ? (
                            <Box></Box>
                        ) : (
                            <Button variant="outline" onClick={() => goto(getAscendUrl())}>
                                Close
                            </Button>
                        )}
                        <Button
                            disabled={!readyToFinish}
                            onClick={completeConnection}
                            leftIcon={finalizing ? <Loader color="gray.0" size={14} /> : null}
                        >
                            {finalizing ? 'Deploying...' : 'Connect Selected'}
                        </Button>
                    </Group>
                </WizardFormFooter>
            )}
        </>
    );
}

interface AzureWebConnectionStatusCellProps {
    connectionStatusSvc: {
        status: EventEmitter<SubProvisionStatus>;
        loadWarning: (subId: string) => void;
    };
    subscription: AzureSubscription;
}
function AzureWebConnectionStatusCell({ connectionStatusSvc, subscription }: AzureWebConnectionStatusCellProps) {
    const modals = useModals();
    const { status, error, warning } = useEventValue(connectionStatusSvc.status)?.[subscription.Id ?? ''] ?? {};
    useEffect(() => connectionStatusSvc.loadWarning(subscription.Id ?? ''), [subscription.Id]);

    const finalStatus: { text: string; color: MantineColor; warning?: string } =
        status === SubDeployStatus.Completed
            ? { text: 'Connected', color: 'primary' }
            : status === SubDeployStatus.Deploying
            ? { text: 'Deploying', color: 'success' }
            : status === SubDeployStatus.Error
            ? { text: 'Connection Failed', color: 'error', warning: error ?? 'Unknown error' }
            : status === SubDeployStatus.Queued
            ? { text: 'Queued', color: 'gray' }
            : warning === 'Connected'
            ? { text: 'Connected', color: 'primary' }
            : warning === 'Eligible'
            ? { text: 'Not Connected', color: 'gray' }
            : warning === 'Unavailable'
            ? {
                  text: 'Not Connected',
                  color: 'gray',
                  warning: 'Connection attempt will likely fail. Impersonated user may have insufficient permissions.',
              }
            : { text: '', color: 'gray' };

    const onClick = error
        ? () => {
              const [description, rawContent] = error.split('Content:').map((s) => s.trim());
              const formatJson = (content: string) => {
                  try {
                      return JSON.stringify(JSON.parse(content), undefined, 2);
                  } catch {
                      return content;
                  }
              };
              const content = formatJson(rawContent);

              modals.openModal({
                  title: `Deployment Error - ${subscription.Name}`,
                  children: (
                      <>
                          <Text size="sm" sx={{ whiteSpace: 'pre-wrap' }}>
                              {description}
                          </Text>
                          {!content ? null : (
                              <Code sx={{ whiteSpace: 'pre-wrap' }} block>
                                  {content}
                              </Code>
                          )}
                      </>
                  ),
              });
          }
        : undefined;

    return (
        <GridFlexCell style={{ padding: 0 }} align="center">
            {!finalStatus.text ? (
                <Skeleton width={80} height={16} />
            ) : (
                <>
                    {finalStatus.warning ? (
                        <Tooltip withinPortal label={finalStatus.warning}>
                            <ActionIcon color="error">
                                <AlertTriangle size={16} />
                            </ActionIcon>
                        </Tooltip>
                    ) : null}
                    <Badge color={finalStatus.color} onClick={onClick} style={{ cursor: onClick ? 'pointer' : 'default' }}>
                        {finalStatus.text}
                    </Badge>
                </>
            )}
        </GridFlexCell>
    );
}

type JobOfGcpProjectProvisioning = TJob<{ ProjectId: string }, 'Cloudsaver.Resources.Domain.Gcp.Models.GcpProjectProvisioning'>;
export function ConnectGcpWizard({
    hideFooter,
    onReadyChanged,
}: {
    hideFooter?: boolean;
    onReadyChanged?: (ready: boolean, tenantId: string) => void;
}) {
    const { getAscendUrl, goto } = useNav();
    const cspConnSvc = useDi(CspConnectionService);
    const notificationSvc = useDi(NotificationService);
    const jobSvc = useDi(JobService);
    const pollSvc = useDi(PollingService);
    const bundleSvc = useDi(AsyncBundler);

    const [connState, setConnState] = useState<LoadingState>(LoadingState.wait);
    const [attemptedConnection, setAttemptedConnection] = useState<boolean>(false);
    const [connMsg, setConnMsg] = useState<string>('Awaiting consent');
    const [projects, setProjects] = useState<Array<GcpProject>>([]);
    const [selectedProjects, setSelectedProjects] = useState<GcpProject[]>([]);
    const [finalizing, setFinalizing] = useState<boolean>(false);
    const [connectionId, setConnectionId] = useState<string>('');
    const [showRequiredColumns, setShowRequiredColumns] = useState<boolean>(false);

    const billingConfigModel = useMemo(() => {
        type BillingConfigTable = GcpBillingTableData & { id: string };
        type BillingConfigItem = {
            id: string;
            name: string;
            projects: string[];
            tableOptions: BillingConfigTable[];
            selectedTable: BillingConfigTable | null;
        };

        const loading = new EventEmitter<boolean>(false);
        const billingConfigs: BillingConfigItem[] = [];
        const requiredColumns: string[] = [];

        const processProjects = (projects: GcpProject[]) =>
            projects.reduce(
                (res, project) => {
                    res.uniqueOrgIds.add(project.OrganizationNumber ?? '');
                    res.projectLookup.set(project.Id ?? '', project);
                    return res;
                },
                { uniqueOrgIds: new Set<string>(), projectLookup: new Map<string, GcpProject>() }
            );
        const createReqKey = (orgIds: string[], connId: string) => JSON.stringify({ orgIds: orgIds.sort(), connId });
        const throttleSvc = RequestThrottlingService.create(300, false);
        const fetchBillingInfo = async (orgIds: string[], connectionId: string) =>
            throttleSvc.throttle(createReqKey(orgIds, connectionId), () => postConnectionGetGcpBillingInfo(orgIds, { connectionId }));
        const getBillingConfig = (infoItem: GcpBillingInfo) =>
            billingConfigs.find((b) => b.id === infoItem.BillingAccountId) ?? {
                id: infoItem.BillingAccountId!,
                name: infoItem.BillingAccountName!,
                projects: [],
                tableOptions: [],
                selectedTable: null,
            };
        const processBillingInfo = (billingInfo: GcpBillingExportInfo, projectLookup: Map<string, GcpProject>) => {
            requiredColumns.splice(0, Infinity, ...(billingInfo.RequiredColumns ?? []));
            const selectedProjectBillings = billingInfo.ProjectBillingInfos?.filter((p) => projectLookup.has(p.ProjectId ?? '')) ?? [];
            return (
                billingInfo.BillingInfos?.map((b) => {
                    const config = getBillingConfig(b);
                    const projects = new Set(
                        selectedProjectBillings.filter((p) => p.BillingAccountId === b.BillingAccountId).map((p) => p.ProjectId!) ?? []
                    );
                    config.projects = [...projects].sort();
                    config.tableOptions = (billingInfo.BillingExportTables?.filter((t) => projects.has(t.ProjectId ?? '')) ?? []).map((t) => ({
                        ...t,
                        id: `${t.ProjectId}.${t.DatasetId}.${t.TableId}`,
                    }));
                    config.selectedTable = config.tableOptions.find((t) => t.id === config.selectedTable?.id) || null;
                    return config;
                }) ?? []
            );
        };

        return {
            loading,
            getRequiredColumns: () => requiredColumns,
            isValid: () => billingConfigs.every((v) => v.selectedTable || v.projects.length === 0),
            items: billingConfigs,
            loadBillingInfo: async (projects: GcpProject[], connectionId: string) => {
                const { uniqueOrgIds, projectLookup } = processProjects(projects);
                try {
                    loading.emit(true);
                    const billingInfo = await fetchBillingInfo([...uniqueOrgIds], connectionId);
                    billingConfigs.splice(0, Infinity, ...processBillingInfo(billingInfo, projectLookup));
                } finally {
                    loading.emit(false);
                }
            },
            selectTable: (config: BillingConfigItem, tableId: string) => {
                config.selectedTable = config.tableOptions.find((t) => t.id === tableId) || null;
                loading.emitCurrent();
            },
            clear: () => {
                billingConfigs.splice(0, Infinity);
                loading.emitCurrent();
            },
        };
    }, []);
    const billingConfigLoading = useEventValue(billingConfigModel.loading);

    const projectLookup = useMemo(() => projects.reduce((res, project) => res.set(project.Id!, project), new Map<string, GcpProject>()), [projects]);
    const getProjectName = useCallback((projectId: string) => projectLookup.get(projectId)?.DisplayName || projectId, [projectLookup]);

    useEffect(() => {
        billingConfigModel.loadBillingInfo(selectedProjects, connectionId);
    }, [selectedProjects, connectionId]);

    const authorizeConnection = async () => {
        const throttleSvc = RequestThrottlingService.create(500);
        const key = 'gcp-consent-auth';
        setConnectionId('');
        setConnState(LoadingState.loading);
        setConnMsg('Waiting for consent...');

        const consentUrlResp = (await throttleSvc.throttle(key, () => postConnectionAuthorizeGcpConnection())) as unknown as {
            connectionId: string;
            authorizeUrl: string;
        };
        setConnectionId(consentUrlResp.connectionId);
        window.open(consentUrlResp.authorizeUrl, '_blank');
    };

    const readyToFinish = selectedProjects.length > 0 && !finalizing;
    const billingValid = !billingConfigLoading && billingConfigModel.isValid();

    const connectionStatusSvc = useMemo(() => {
        const statusEvt = new EventEmitter<SubProvisionStatus>({});
        return {
            status: statusEvt,
            updateStatuses(updates: { projectId: string; status: SubDeployStatus; error?: string | null }[]) {
                statusEvt.emit({
                    ...statusEvt.value,
                    ...updates.reduce((res, u) => {
                        res[u.projectId] = { ...statusEvt.value[u.projectId], status: u.status, error: u.error };
                        return res;
                    }, {} as SubProvisionStatus),
                });
            },
            loadWarning(projectId: string) {
                if (typeof statusEvt.value[projectId]?.warning !== 'string') {
                    const getSubStatuses = async (projectIds: string[]) => {
                        const result = await postConnectionGetGcpProjectConnectabilityStatus(projectIds, {
                            connectionId: connectionId ?? '',
                        });
                        const updates = result.reduce((res, u) => {
                            res[u.projectId!] = { ...statusEvt.value[u.projectId!], warning: u.status ?? '' };
                            return res;
                        }, {} as SubProvisionStatus);
                        statusEvt.emit({ ...statusEvt.value, ...updates });
                    };
                    bundleSvc.bundle<string>(connectionId!, projectId, getSubStatuses);
                    statusEvt.emit({ ...statusEvt.value, [projectId]: { ...statusEvt.value[projectId], warning: '' } });
                }
            },
        };
    }, [connectionId]);

    const projectColumns = useMemo(() => {
        return [
            {
                id: 'Status',
                header: 'Status',
                accessor: (s) => connectionStatusSvc.status.value[s.Id!].status,
                sortField: 'Status',
                type: 'string',
                align: 'center',
                noSort: true,
                defaultWidth: 165,
                noRemove: true,
                cellRenderer: (p) => <GcpWebConnectionStatusCell connectionStatusSvc={connectionStatusSvc} project={p} />,
                exportOptions: {
                    hidden: true,
                },
            },
            {
                id: 'Name',
                header: 'Name',
                accessor: (s) => s.DisplayName,
                sortField: 'Name',
                type: 'string',
                defaultWidth: 300,
                noRemove: true,
                filter: {
                    filterType: 'string',
                    filterField: 'Name',
                    name: 'Name',
                },
            },
            {
                id: 'Id',
                header: 'Project Id',
                accessor: (s) => s.Id,
                sortField: 'Id',
                type: 'string',
                defaultWidth: 300,
                noRemove: true,
                filter: {
                    filterType: 'string',
                    filterField: 'Id',
                    name: 'Id',
                },
            },
            {
                id: 'Organization',
                header: 'Organization',
                accessor: (s) => `${s.OrganizationName}/${s.OrganizationNumber}`,
                sortField: 'Organization',
                type: 'string',
                defaultWidth: 300,
                noRemove: true,
                filter: {
                    filterType: 'string',
                    filterField: 'Organization',
                    name: 'Organization',
                },
            },
        ] as DataColumnConfig<GcpProject>[];
    }, [connectionId, projects]);

    const poller = useMemo(
        () => ({
            pendingConn: null as GcpConnectionStatus | null,
            shouldPoll: true,
            async poll() {
                setTimeout(async () => {
                    if (!this.shouldPoll || !connectionId) return;
                    try {
                        if (this.pendingConn && this.pendingConn.Connected) {
                            setConnState(LoadingState.check);
                            setConnMsg('Consent successful');
                            setProjects((this.pendingConn.Projects ?? []).filter((p) => p.OrganizationNumber));
                            this.shouldPoll = false;
                            cspConnSvc.connectionStateChanged.emit();
                        } else {
                            setProjects([]);
                            setSelectedProjects([]);
                            setConnState(LoadingState.wait);
                            billingConfigModel.clear();
                            this.pendingConn = await getConnectionGetGcpConnectionStatus({ connectionId: connectionId! });
                        }
                    } catch {
                        setConnState(LoadingState.failed);
                        setConnMsg('Error while checking consent');
                    } finally {
                        this.poll();
                    }
                }, 3000);
            },
            stopPolling() {
                this.shouldPoll = false;
            },
        }),
        [connectionId]
    );

    useEffect(() => {
        if (!connectionId) return;
        poller.shouldPoll = true;
        poller.poll();
        return () => poller.stopPolling();
    }, [connectionId, poller]);

    const completeConnection = async () => {
        if (!connectionId) return;
        try {
            setFinalizing(true);

            const selectedProjectIds = selectedProjects.map((p) => p.Id!);
            const billingAccounts = billingConfigModel.items
                .filter((b) => b.projects.length && b.selectedTable)
                .map(
                    ({ selectedTable: t, id, name }) =>
                        ({
                            BillingAccountId: id,
                            Name: name,
                            DatasetId: t?.DatasetId,
                            ExportDataTable: t?.id,
                            TableId: t?.TableId,
                        } as GcpBillingAccount)
                );
            const request: GcpConnectionRequest = { ConnectionId: connectionId, ProjectIds: selectedProjectIds, BillingAccounts: billingAccounts };
            var job = await postConnectionCompleteGcpConnection(request);
            if (job.FailureMessage) {
                notificationSvc.notify('Failed to start connection process', job.FailureMessage ?? '', 'error', null);
            }

            beginJobPolling(job.HierarchyId ?? '');
        } catch {
            setFinalizing(false);
            notificationSvc.notify('Failed to start connection process. Please try again.', '', 'error', null);
        }
    };

    const beginJobPolling = (jobHierarchyId: string) => {
        const poller = pollSvc.pollUntil(
            async () => {
                const jobs = (await jobSvc.queryJobs((q) =>
                    q.where((b) => b.model.HierarchyId!.eq(jobHierarchyId)).take(10000)
                )) as JobOfGcpProjectProvisioning[];

                connectionStatusSvc.updateStatuses(
                    jobs.map((j) => {
                        const status =
                            j.Status === 'Failed'
                                ? SubDeployStatus.Error
                                : j.Status === 'Succeeded'
                                ? SubDeployStatus.Completed
                                : j.Status === 'Started'
                                ? SubDeployStatus.Deploying
                                : SubDeployStatus.Queued;
                        return { projectId: j.Parameters.ProjectId, status, error: j.FailureMessage };
                    })
                );
                return jobs;
            },
            (jobs) => jobs.length > 0 && jobs.every((j) => j.FinishedAt),
            1500
        );

        poller
            .then(() => {
                setFinalizing(false);
            })
            .catch((err) => {
                setFinalizing(false);
                notificationSvc.notify('Failed to monitor deployments.', String(err ?? ''), 'error', null);
            })
            .finally(() => setAttemptedConnection(true));
    };

    return (
        <>
            <WizardFormStep title="1. Sign in with Google" subTitle="A new tab will open prompting you to log in and grant consent for Cloudsaver.">
                <Group spacing="xs" ml="xl">
                    <Button
                        component="a"
                        onClick={() => authorizeConnection()}
                        rightIcon={<ExternalLink size={16} />}
                        target="_blank"
                        rel="noreferrer"
                        data-atid="GcpConsentButton"
                    >
                        Open Google Consent
                    </Button>
                </Group>
                <Box ml="lg" mt="sm">
                    <LoadingValidation loadingState={connState} text={connMsg} />
                </Box>
            </WizardFormStep>
            <WizardFormStep
                title="2. Select the projects to connect"
                subTitle="After consent is granted, we'll list projects visible to the granted scope."
            >
                <Box ml="xl" sx={{ display: 'flex', flexDirection: 'column', minHeight: 0 }}>
                    {projects.length === 0 ? (
                        <Text size="sm" color="dimmed">
                            No projects available yet. Complete consent first.
                        </Text>
                    ) : (
                        (() => {
                            const visibleRows = Math.min(projects.length, 10);
                            const gridHeight = 150 + visibleRows * 30;
                            const maxHeight = 600;

                            return (
                                <Box
                                    sx={{
                                        height: gridHeight,
                                        maxHeight,
                                        display: 'flex',
                                        flexDirection: 'column',
                                        minHeight: 180,
                                    }}
                                >
                                    <DataGrid
                                        columns={projectColumns}
                                        dataSource={projects}
                                        selectionMode="multiple"
                                        showCount
                                        showNumSelected
                                        hideColumnSelector
                                        allowSavedViews={false}
                                        rowIdProvider={(r) => r.Id!}
                                        onSelectedChanged={async ({ getItems }) => {
                                            const selected = await getItems();
                                            setSelectedProjects(selected);
                                        }}
                                    ></DataGrid>
                                </Box>
                            );
                        })()
                    )}
                </Box>
            </WizardFormStep>
            <WizardFormStep
                title="3. Configure Billing Export"
                subTitle={<>Select the BigQuery table containing billing export for each Billing Account.</>}
            >
                <Box ml="xl">
                    <FillerSwitch loading={!!selectedProjects.length && billingConfigLoading && !billingConfigModel.items.length}>
                        {() =>
                            !selectedProjects.length ? (
                                <Text size="sm" color="dimmed">
                                    Select projects in the Step 2 to load billing export options.
                                </Text>
                            ) : (
                                <>
                                    <Text color="dimmed">
                                        If no valid options are displayed for a billing account, please use the{' '}
                                        <Tooltip label="Open Google Cloud documentation in a new Tab.">
                                            <Anchor target="_blank" href="https://cloud.google.com/billing/docs/how-to/export-data-bigquery-setup">
                                                setup guide
                                            </Anchor>
                                        </Tooltip>
                                        . Valid tables have these{' '}
                                        <Tooltip label="Show required columns">
                                            <AnchorButton onClick={() => setShowRequiredColumns(true)} text="required columns" />
                                        </Tooltip>
                                        .
                                    </Text>
                                    <Card withBorder py="md" px={0} radius="lg" mt="md">
                                        <Stack>
                                            {billingConfigModel.items
                                                .filter((c) => c.projects.length)
                                                .map((config, index) => {
                                                    const projectCt = config.projects.length;
                                                    const tableOptions = config.tableOptions
                                                        .filter((t) => t.HasRequiredSchema)
                                                        .map((t) => ({
                                                            value: t.id,
                                                            label: t.TableId!,
                                                            dataset: t.DatasetId,
                                                            group: getProjectName(t.ProjectId ?? ''),
                                                        }))
                                                        .sort((a, b) => a.label.localeCompare(b.label));
                                                    return (
                                                        <>
                                                            {index > 0 ? <Divider color="gray.2" /> : null}
                                                            <Box px="lg" key={config.id}>
                                                                <Group position="apart" noWrap>
                                                                    <Box>
                                                                        <Title order={5}>{config.name}</Title>
                                                                        <Text size="sm" color="dimmed">
                                                                            Billing Account ID: {config.id}
                                                                        </Text>
                                                                    </Box>

                                                                    <Select
                                                                        withinPortal
                                                                        placeholder="Select billing export table"
                                                                        data={tableOptions}
                                                                        value={config.selectedTable?.id}
                                                                        onChange={(value) => billingConfigModel.selectTable(config, value!)}
                                                                        clearable
                                                                        size="sm"
                                                                        sx={{ minWidth: 400 }}
                                                                        error={tableOptions.length ? null : 'No valid billing export tables found'}
                                                                        dropdownComponent="div"
                                                                        itemComponent={({ label, dataset, ...props }) => (
                                                                            <Box {...props}>
                                                                                <Text size="xs">{dataset}</Text>
                                                                                <Text size="sm">{label}</Text>
                                                                            </Box>
                                                                        )}
                                                                    />
                                                                </Group>
                                                                <Text size="xs" color="dimmed" mt="xs">
                                                                    {projectCt} selected project{projectCt > 1 ? 's: ' : ': '}
                                                                    {config.projects
                                                                        .map((p) => getProjectName(p))
                                                                        .slice(0, 10)
                                                                        .join(', ')}
                                                                    {projectCt > 10 ? ` , (...${config.projects.length - 10} others)` : ''}
                                                                </Text>
                                                            </Box>
                                                        </>
                                                    );
                                                })}
                                        </Stack>
                                    </Card>
                                </>
                            )
                        }
                    </FillerSwitch>
                </Box>
            </WizardFormStep>
            <Modal
                onClose={setShowRequiredColumns.bind(null, false)}
                opened={showRequiredColumns}
                title="Required Columns in Billing Export Table"
                size="lg"
            >
                <Group spacing="xs">
                    {billingConfigModel.getRequiredColumns().map((column) => (
                        <Badge key={column} variant="outline" size="sm" sx={{ textTransform: 'none' }}>
                            {column}
                        </Badge>
                    ))}
                </Group>
            </Modal>
            {hideFooter ? null : (
                <WizardFormFooter>
                    <Group position="apart">
                        {!attemptedConnection ? (
                            <Box></Box>
                        ) : (
                            <Button variant="outline" onClick={() => goto(getAscendUrl())}>
                                Close
                            </Button>
                        )}
                        <Button
                            disabled={!readyToFinish}
                            onClick={completeConnection}
                            data-disabled={!billingValid ? 'true' : undefined}
                            leftIcon={finalizing ? <Loader color="gray.0" size={14} /> : null}
                        >
                            {finalizing ? 'Connecting...' : 'Connect Selected'}
                        </Button>
                    </Group>
                </WizardFormFooter>
            )}
        </>
    );
}

interface GcpWebConnectionStatusCellProps {
    connectionStatusSvc: {
        status: EventEmitter<SubProvisionStatus>;
        loadWarning: (projectId: string) => void;
    };
    project: GcpProject;
}
function GcpWebConnectionStatusCell({ connectionStatusSvc, project }: GcpWebConnectionStatusCellProps) {
    const modals = useModals();
    const { status, error, warning } = useEventValue(connectionStatusSvc.status)?.[project.Id ?? ''] ?? {};
    useEffect(() => connectionStatusSvc.loadWarning(project.Id ?? ''), [project.Id]);

    const finalStatus: { text: string; color: MantineColor; warning?: string } =
        status === SubDeployStatus.Completed
            ? { text: 'Connected', color: 'primary' }
            : status === SubDeployStatus.Deploying
            ? { text: 'Deploying', color: 'success' }
            : status === SubDeployStatus.Error
            ? { text: 'Connection Failed', color: 'error', warning: error ?? 'Unknown error' }
            : status === SubDeployStatus.Queued
            ? { text: 'Queued', color: 'gray' }
            : warning === 'Connected'
            ? { text: 'Connected', color: 'primary' }
            : warning === 'Eligible'
            ? { text: 'Not Connected', color: 'gray' }
            : warning === 'Unavailable'
            ? {
                  text: 'Not Connected',
                  color: 'gray',
                  warning: 'Connection attempt will likely fail. Impersonated user may have insufficient permissions.',
              }
            : { text: '', color: 'gray' };

    const onClick = error
        ? () => {
              const [description, rawContent] = error.split('Content:').map((s) => s.trim());
              const formatJson = (content: string) => {
                  try {
                      return JSON.stringify(JSON.parse(content), undefined, 2);
                  } catch {
                      return content;
                  }
              };
              const content = formatJson(rawContent);

              modals.openModal({
                  title: `Deployment Error - ${project.DisplayName}`,
                  children: (
                      <>
                          <Text size="sm" sx={{ whiteSpace: 'pre-wrap' }}>
                              {description}
                          </Text>
                          {!content ? null : (
                              <Code sx={{ whiteSpace: 'pre-wrap' }} block>
                                  {content}
                              </Code>
                          )}
                      </>
                  ),
              });
          }
        : undefined;

    return (
        <GridFlexCell style={{ padding: 0 }} align="center">
            {!finalStatus.text ? (
                <Skeleton width={80} height={16} />
            ) : (
                <>
                    {finalStatus.warning ? (
                        <Tooltip withinPortal label={finalStatus.warning}>
                            <ActionIcon color="error">
                                <AlertTriangle size={16} />
                            </ActionIcon>
                        </Tooltip>
                    ) : null}
                    <Badge color={finalStatus.color} onClick={onClick} style={{ cursor: onClick ? 'pointer' : 'default' }}>
                        {finalStatus.text}
                    </Badge>
                </>
            )}
        </GridFlexCell>
    );
}
